/**
 * Адреса и права раздела «Обучение» портала (этап P4, §2 ТЗ портала).
 *
 * `/study/<вкладка>/<подвкладка или объект>`:
 *
 * - `/study/catalog`, `/study/catalog/42` — справочник и карточка блюда;
 * - `/study/courses`, `/study/courses/7` — мои курсы и курс;
 * - `/study/mentor/<chat|metrics|goals|micro|roleplay>` — Наставник (D186);
 * - `/study/editor/<dishes|courses|knowledge|scenarios|micro>` — Редактор;
 * - `/study/reports` — отчёты S1–S8.
 *
 * **Какие вкладки видны — те же правила, что у Mini App** (`App.tsx`,
 * `Editor.tsx`, `Reports.tsx` сборки `study`), только права берутся из
 * `GET /api/platform/navigation`, а не из `/api/study/me`: владелец и
 * управляющий сервиса видят всё, остальные — по правам D181. Это
 * оформление: `study-api` проверяет право на каждом запросе.
 */

import type { Navigation } from '../../api';

export interface StudyAccess {
  catalog: boolean;
  courses: boolean;
  /** Вопросы по регламентам — разговор Наставника. */
  assistant: boolean;
  /** Показатели, цели, микроурок, тренировка. */
  mentor: boolean;
  editDishes: boolean;
  editCourses: boolean;
  editKnowledge: boolean;
  /** «Отчёты по обучению» — своя галочка (D235). */
  reports: boolean;
  /** Уровень `admin` или владелец — панель наставника `S7`. */
  manager: boolean;
  /** Владелец системы — сводка собственнику `S8`. */
  owner: boolean;
}

export function studyAccess(nav: Navigation): StudyAccess | null {
  const study = nav.services.study;
  const host = nav.user.is_host;
  if (!study && !host) return null;
  const admin = host || study?.level === 'admin' || study?.level === 'host';
  const rights = study?.rights ?? {};
  const has = (code: string) => admin || Boolean(rights[code]);
  const edit = (code: string) => admin || rights[code] === 'edit';
  return {
    catalog: has('dishes'),
    courses: has('courses'),
    assistant: has('assistant'),
    mentor: has('mentor'),
    editDishes: edit('dishes'),
    editCourses: edit('courses'),
    editKnowledge: edit('assistant'),
    reports: has('reports'),
    manager: admin,
    owner: host,
  };
}

export type TabCode = 'catalog' | 'courses' | 'mentor' | 'editor' | 'reports';

export interface Tab<C extends string = string> {
  code: C;
  title: string;
}

export function tabsFor(access: StudyAccess): Tab<TabCode>[] {
  const editor = editorTabs(access).length > 0;
  const reports = access.reports;
  return [
    ...(access.catalog ? [{ code: 'catalog' as const, title: 'Справочник' }] : []),
    ...(access.courses ? [{ code: 'courses' as const, title: 'Курсы' }] : []),
    ...(access.assistant || access.mentor ? [{ code: 'mentor' as const, title: 'Наставник' }] : []),
    ...(editor ? [{ code: 'editor' as const, title: 'Редактор' }] : []),
    ...(reports ? [{ code: 'reports' as const, title: 'Отчёты' }] : []),
  ];
}

export type MentorTab = 'chat' | 'metrics' | 'goals' | 'micro' | 'roleplay';

/** D186: разговор первым; развитие — только при праве `mentor`. */
export function mentorTabs(access: StudyAccess): Tab<MentorTab>[] {
  return [
    ...(access.assistant ? [{ code: 'chat' as const, title: 'Разговор' }] : []),
    ...(access.mentor
      ? [
          { code: 'metrics' as const, title: 'Показатели' },
          { code: 'goals' as const, title: 'Цели' },
          { code: 'micro' as const, title: 'Микроурок' },
          { code: 'roleplay' as const, title: 'Тренировка' },
        ]
      : []),
  ];
}

export type EditorTab = 'dishes' | 'courses' | 'knowledge' | 'scenarios' | 'micro';

/** Как в `Editor.tsx`: сценарии и микроуроки правит тот, кто правит курсы. */
export function editorTabs(access: StudyAccess): Tab<EditorTab>[] {
  return [
    ...(access.editDishes ? [{ code: 'dishes' as const, title: 'Блюда' }] : []),
    ...(access.editCourses ? [{ code: 'courses' as const, title: 'Курсы' }] : []),
    ...(access.editKnowledge ? [{ code: 'knowledge' as const, title: 'Регламенты' }] : []),
    ...(access.editCourses ? [{ code: 'scenarios' as const, title: 'Тренировки' }] : []),
    ...(access.editCourses ? [{ code: 'micro' as const, title: 'Микроуроки' }] : []),
  ];
}

export interface StudyRoute {
  /** Вкладка из адреса, как написана; доступность решает экран. */
  tab: string | null;
  sub: string | null;
  /** Числовой объект: блюдо или курс. */
  id: number | null;
}

export function readStudy(path: string): StudyRoute {
  const [, , tab, sub] = path.split('/');
  const id = sub && /^\d+$/.test(sub) ? Number(sub) : null;
  return { tab: tab || null, sub: sub && id === null ? sub : null, id };
}

export function studyPath(tab: TabCode, rest?: string | number | null): string {
  return rest === undefined || rest === null ? `/study/${tab}` : `/study/${tab}/${rest}`;
}

/** Выбрать доступную вкладку: из адреса, иначе первую. */
export function pick<C extends string>(tabs: Tab<C>[], wanted: string | null): C | null {
  return tabs.find((tab) => tab.code === wanted)?.code ?? tabs[0]?.code ?? null;
}
